import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "@/components/icons";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { AuthCard } from "./Login";

/**
 * VerifyEmail — estilo minimalista "Files Management".
 * Fondo blanco full-screen. Paleta blanco / gris #F2F3F5 / negro.
 *
 * Landing page reached from the verification email link.
 */

// Module-level dedup: the verify-email endpoint is one-shot (the token is
// deleted on success), so we must guarantee a single network call per token
// even when React Strict Mode (dev) double-invokes the effect.
const VERIFY_PROMISES = new Map();

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const token = params.get("token") || "";
  const [state, setState] = useState(() =>
    token
      ? { status: "loading", message: "" }
      : { status: "error", message: "Falta el token de verificación." }
  );

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let promise = VERIFY_PROMISES.get(token);
    if (!promise) {
      promise = api.post("/auth/verify-email", { token });
      VERIFY_PROMISES.set(token, promise);
    }
    (async () => {
      try {
        const r = await promise;
        if (cancelled) return;
        setState({
          status: "ok",
          message: `${r.data.email} ha sido verificado correctamente.`,
        });
        let fresh = null;
        if (refresh) {
          try {
            fresh = await refresh();
          } catch {
            /* ignore */
          }
        }
        setTimeout(() => {
          if (cancelled) return;
          const u = fresh || user;
          if (!u) {
            nav("/login", { replace: true });
            return;
          }
          if (u.role === "doctor") {
            nav("/onboarding", { replace: true });
            return;
          }
          const dest = {
            admin: "/admin",
            secretary: "/secretaria",
            pharmacy: "/farmacia",
          }[u.role];
          nav(dest || "/", { replace: true });
        }, 1500);
      } catch (e) {
        if (cancelled) return;
        let fresh = null;
        if (refresh) {
          try {
            fresh = await refresh();
          } catch {
            /* ignore */
          }
        }
        if (fresh?.email_verified) {
          setState({
            status: "ok",
            message: "Tu correo ya está verificado.",
          });
          setTimeout(() => {
            if (cancelled) return;
            if (fresh.role === "doctor") {
              nav("/onboarding", { replace: true });
              return;
            }
            const dest = {
              admin: "/admin",
              secretary: "/secretaria",
              pharmacy: "/farmacia",
            }[fresh.role];
            nav(dest || "/", { replace: true });
          }, 1200);
          return;
        }
        setState({
          status: "error",
          message:
            formatErr(e.response?.data?.detail) ||
            "El enlace no es válido o ya fue utilizado.",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, refresh, nav, user]);

  const goHome = () => {
    if (!user) return nav("/login", { replace: true });
    const dest = {
      admin: "/admin",
      doctor: "/doctor",
      secretary: "/secretaria",
      pharmacy: "/farmacia",
    }[user.role];
    nav(dest || "/", { replace: true });
  };

  return (
    <div className="min-h-screen w-full bg-white flex items-center justify-center p-4 sm:p-6">
      <AuthCard>
        <div data-testid="verify-email-card" className="text-center">
          {state.status === "loading" && (
            <>
              <div className="w-12 h-12 mx-auto rounded-full bg-[#F2F3F5] flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-black animate-spin" />
              </div>
              <h1 className="mt-6 text-2xl sm:text-[28px] font-semibold text-black tracking-tight">
                Verificando tu correo…
              </h1>
              <p className="mt-3 text-sm text-black/60">
                Esto solo tomará unos segundos.
              </p>
            </>
          )}

          {state.status === "ok" && (
            <>
              <div className="w-12 h-12 mx-auto rounded-full bg-[#F2F3F5] flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-black" />
              </div>
              <h1 className="mt-6 text-2xl sm:text-[28px] font-semibold text-black tracking-tight">
                ¡Correo verificado!
              </h1>
              <p
                className="mt-3 text-sm text-black/60"
                data-testid="verify-success-message"
              >
                {state.message}
              </p>
              <p className="mt-3 inline-flex items-center gap-2 text-xs text-black/50">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Redirigiéndote…
              </p>
              <button
                type="button"
                onClick={goHome}
                data-testid="verify-go-home-btn"
                className="mt-6 w-full bg-black hover:bg-black/90 text-white rounded-xl py-3.5 text-[15px] font-semibold transition"
              >
                {user ? "Ir a mi panel" : "Iniciar sesión"}
              </button>
            </>
          )}

          {state.status === "error" && (
            <>
              <div className="w-12 h-12 mx-auto rounded-full bg-[#F2F3F5] flex items-center justify-center">
                <XCircle className="w-6 h-6 text-black" />
              </div>
              <h1 className="mt-6 text-2xl sm:text-[28px] font-semibold text-black tracking-tight">
                No pudimos verificar tu correo
              </h1>
              <p
                className="mt-3 text-sm text-black/60"
                data-testid="verify-error-message"
              >
                {state.message}
              </p>
              <div className="mt-6 space-y-3">
                {user ? (
                  <button
                    type="button"
                    onClick={goHome}
                    className="w-full bg-black hover:bg-black/90 text-white rounded-xl py-3.5 text-[15px] font-semibold transition"
                  >
                    Volver a mi panel
                  </button>
                ) : (
                  <Link
                    to="/login"
                    className="block w-full bg-black hover:bg-black/90 text-white rounded-xl py-3.5 text-[15px] font-semibold transition text-center"
                  >
                    Iniciar sesión
                  </Link>
                )}
                <span className="block text-xs text-black/50 leading-relaxed">
                  Si ya iniciaste sesión, puedes solicitar un nuevo correo
                  desde el banner amarillo en la parte superior.
                </span>
              </div>
            </>
          )}
        </div>
      </AuthCard>
    </div>
  );
}
